import React, { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Chip,
  Popover,
  TextField,
  Autocomplete,
  Typography,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';

function chipSx(tag, compact) {
  return {
    height: compact ? 22 : 26,
    maxWidth: compact ? 140 : 220,
    bgcolor: tag.color || '#DDE8E4',
    color: '#3D4F4A',
    fontWeight: 500,
    border: '1px solid rgba(61, 79, 74, 0.12)',
    '& .MuiChip-label': {
      px: compact ? 0.75 : 1,
      fontSize: compact ? '0.7rem' : '0.8125rem',
    },
    '& .MuiChip-deleteIcon': {
      color: 'rgba(61, 79, 74, 0.55)',
      fontSize: compact ? 14 : 16,
      '&:hover': { color: 'rgba(61, 79, 74, 0.85)' },
    },
  };
}

/**
 * Редактор тегов в стиле Notion: чипы, поиск, создание по Enter.
 */
export default function ArtworkTagsEditor({
  artworkId,
  tags = [],
  editable = false,
  compact = false,
}) {
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const open = Boolean(anchorEl);

  const { data: catalogTags = [] } = useQuery(
    ['tags', inputValue],
    async () => {
      const params = { limit: 40 };
      if (inputValue.trim()) params.q = inputValue.trim();
      const res = await axios.get('/api/tags', { params });
      return res.data;
    },
    { enabled: editable && open, staleTime: 20_000 }
  );

  const saveMutation = useMutation(
    async (names) => {
      const res = await axios.put(`/api/artworks/${artworkId}/tags`, { tags: names });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('artworks');
        queryClient.invalidateQueries(['artwork', String(artworkId)]);
        queryClient.invalidateQueries('tags');
        queryClient.invalidateQueries('lists_artworks');
        queryClient.invalidateQueries('statistics');
      },
    }
  );

  const currentNames = (tags || []).map((t) => t.name);
  const currentLower = new Set(currentNames.map((n) => n.toLowerCase()));

  const persist = (names) => {
    if (!editable || saveMutation.isLoading) return;
    saveMutation.mutate(names);
  };

  const removeTag = (name) => {
    persist(currentNames.filter((n) => n !== name));
  };

  const addTag = (rawName) => {
    const name = rawName.trim().replace(/\s+/g, ' ');
    if (!name || name.length > 64) return;
    if (currentLower.has(name.toLowerCase())) {
      setInputValue('');
      return;
    }
    persist([...currentNames, name]);
    setInputValue('');
  };

  const handleOpen = (event) => {
    if (!editable) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setInputValue('');
  };

  const trimmedInput = inputValue.trim();
  const canCreate =
    trimmedInput.length > 0 && !currentLower.has(trimmedInput.toLowerCase());

  const suggestions = catalogTags
    .map((t) => t.name)
    .filter((name) => !currentLower.has(name.toLowerCase()))
    .filter((name) => {
      if (!trimmedInput) return true;
      return name.toLowerCase().includes(trimmedInput.toLowerCase());
    });

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        minWidth: compact ? 100 : 160,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {(tags || []).map((tag) => (
        <Chip
          key={tag.id ?? tag.name}
          label={tag.name}
          size="small"
          onDelete={editable ? () => removeTag(tag.name) : undefined}
          sx={chipSx(tag, compact)}
        />
      ))}

      {editable && (
        <Chip
          icon={<AddIcon sx={{ fontSize: compact ? 14 : 16 }} />}
          label={compact ? undefined : 'Добавить'}
          size="small"
          variant="outlined"
          onClick={handleOpen}
          disabled={saveMutation.isLoading}
          sx={{
            height: compact ? 22 : 26,
            borderStyle: 'dashed',
            color: 'text.secondary',
            '& .MuiChip-icon': { ml: compact ? 0.5 : undefined },
          }}
        />
      )}

      {!editable && (!tags || tags.length === 0) && (
        <Typography variant="caption" color="text.secondary">
          —
        </Typography>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 1.5, width: 300 }}>
          <Autocomplete
            freeSolo
            openOnFocus
            options={suggestions}
            inputValue={inputValue}
            onInputChange={(_, value) => setInputValue(value)}
            onChange={(_, value) => {
              if (value) addTag(value);
            }}
            loading={saveMutation.isLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={inputRef}
                size="small"
                placeholder="Найти или создать тег…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canCreate) {
                    e.preventDefault();
                    addTag(trimmedInput);
                  }
                }}
              />
            )}
            noOptionsText={canCreate ? 'Нажмите Enter, чтобы создать' : 'Нет совпадений'}
            filterOptions={(opts) => opts}
          />
          {canCreate && (
            <ListItemButton
              dense
              onClick={() => addTag(trimmedInput)}
              sx={{ mt: 0.5, borderRadius: 1 }}
            >
              <ListItemText
                primary={`Создать «${trimmedInput}»`}
                primaryTypographyProps={{ variant: 'body2', color: 'primary' }}
              />
            </ListItemButton>
          )}
        </Box>
      </Popover>
    </Box>
  );
}

/**
 * Только отображение тегов (без редактирования).
 */
export function ArtworkTagsDisplay({ tags, compact = false }) {
  return (
    <ArtworkTagsEditor tags={tags} editable={false} compact={compact} artworkId={null} />
  );
}
